import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signIn } from "../lib/auth-client.ts";
import { trackLogin } from "../lib/analytics.ts";
import { AuthDivider, GoogleButton } from "../components/GoogleButton.tsx";
import { PasswordField } from "../components/PasswordField.tsx";
import { ResendVerificationButton } from "../components/ResendVerificationButton.tsx";
import { authResultError, isEmailVerificationError, normalizeAuthError, resultNeedsEmailVerification, type AuthBanner } from "../lib/auth-messages.ts";

const LOGIN_RATE_LIMIT_MESSAGE = "Too many login attempts. Please wait 1 minute before trying again.";

export function LoginPage() {
  const navigate = useNavigate();
  // Return here after sign-in (e.g. the MCP /connect/mcp consent page, with its
  // OAuth params) instead of always landing on /dashboard.
  const next = new URLSearchParams(window.location.search).get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<AuthBanner | null>(null);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setVerificationNeeded(false);
    setLoading(true);
    try {
      let handledByCallback = false;
      const showSignInError = (err: unknown) => {
        handledByCallback = true;
        setPassword("");
        setError(normalizeAuthError(err, "Sign in failed. Please try again.", { rateLimited: LOGIN_RATE_LIMIT_MESSAGE }));
        setVerificationNeeded(isEmailVerificationError(err));
      };

      const res = await signIn.email(
        { email, password },
        { onError: (ctx: { error: unknown }) => showSignInError(ctx.error) },
      );
      if (handledByCallback) return;

      const message = authResultError(res, "Sign in failed. Please try again.");
      if (message) {
        setPassword("");
        setError(message);
        setVerificationNeeded(resultNeedsEmailVerification(res));
        return;
      }
      trackLogin("email");
      navigate(next || "/dashboard", { replace: true });
    } catch (err) {
      setPassword("");
      setError(normalizeAuthError(err, "Sign in failed. Please try again.", { rateLimited: LOGIN_RATE_LIMIT_MESSAGE }));
      setVerificationNeeded(isEmailVerificationError(err));
    } finally {
      setLoading(false);
    }
  }


  // Enterprise SSO: route by the email's domain to the org's IdP. The client
  // redirects to the provider; on return the session lands on /dashboard.
  async function handleSso() {
    setError(null);
    setNotice(null);
    setVerificationNeeded(false);
    if (!email) {
      setError("Enter your work email, then continue with Single Sign-On.");
      return;
    }
    setLoading(true);
    try {
      const res = (await signIn.sso({
        email,
        callbackURL: `${window.location.origin}${next || "/dashboard"}`,
      })) as unknown as { data?: { url?: string }; error?: { message?: string; code?: string } } | undefined;
      if (res?.error) {
        setError(normalizeAuthError(res.error, "No SSO provider is configured for that email domain."));
        return;
      }
      if (res?.data?.url) window.location.href = res.data.url;
    } catch (err) {
      setError(normalizeAuthError(err, "No SSO provider for that email domain."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap animate-fade-in">
        <div className="auth-logo">
          <div className="auth-logo-mark">
            <img src="/logo.webp" alt="CurrencyCore" />
          </div>
          <h1>Welcome back</h1>
          <p>Sign in to your CurrencyCore account</p>
        </div>

        <div className="auth-card">
          {error && <div className="auth-banner auth-banner-error">{error}</div>}
          {notice && <div className={`auth-banner auth-banner-${notice.kind}`}>{notice.text}</div>}

          <GoogleButton onError={setError} />
          <AuthDivider />

          <form onSubmit={handleSubmit} className="form-stack">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setVerificationNeeded(false);
                  setNotice(null);
                }}
                className="form-input"
                placeholder="you@example.com"
              />
              {verificationNeeded && (
                <ResendVerificationButton
                  email={email}
                  callbackURL={`${window.location.origin}${next || "/dashboard"}`}
                  onError={setError}
                  onSuccess={(text) => {
                    setVerificationNeeded(true);
                    setNotice({ kind: "success", text });
                  }}
                />
              )}
            </div>

            <PasswordField
              id="password"
              label="Password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            <div className="auth-row auth-row-end">
              <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-block"
            >
              {loading ? (
                <>
                  <span className="spinner" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <button
            type="button"
            onClick={() => void handleSso()}
            disabled={loading}
            className="btn btn-ghost btn-block"
            style={{ marginTop: "10px" }}
          >
            Sign in with Single Sign-On
          </button>
        </div>

        <p className="auth-footer">
          Don&apos;t have an account?{" "}
          <Link to={next ? `/signup?next=${encodeURIComponent(next)}` : "/signup"}>Create one</Link>
        </p>
      </div>
    </div>
  );
}
